import { useEffect, useMemo } from "react"

import {
  DEFAULT_BREAKPOINTS,
  HorizontalBreakpoint
} from "shared/types/Breakpoints"

import { ContainerDimensions, useDimensions } from "./useDimensions"

export interface MaxContainerDimensions {
  minWidth?: number // in rem
  maxWidth?: number // in rem
  minHeight?: number // in rem
  maxHeight?: number // in rem
}

export interface UseBreakpointsOptions<
  TBreakpointName extends string = string
> {
  onChange?: (breakpoints: Record<string, boolean>) => void
  breakpoints?: Record<TBreakpointName, MaxContainerDimensions>
}

// Helper function to check if a breakpoint is met
function isBreakpointMet(
  dimensions: ContainerDimensions,
  breakpoint: MaxContainerDimensions
): boolean {
  const { width, height } = dimensions

  // Check minimum width
  if (breakpoint.minWidth !== undefined && width < breakpoint.minWidth) {
    return false
  }

  // Check maximum width
  if (breakpoint.maxWidth !== undefined && width > breakpoint.maxWidth) {
    return false
  }

  // Check minimum height
  if (breakpoint.minHeight !== undefined && height < breakpoint.minHeight) {
    return false
  }

  // Check maximum height
  if (breakpoint.maxHeight !== undefined && height > breakpoint.maxHeight) {
    return false
  }

  return true
}

export function useBreakpoints<
  TBreakpointName extends string = HorizontalBreakpoint
>(
  element: HTMLElement | null,
  options?: UseBreakpointsOptions<TBreakpointName>
): Record<TBreakpointName, boolean> {
  const dimensions = useDimensions(element)
  const { breakpoints = DEFAULT_BREAKPOINTS, onChange } = options || {}

  const breakpointStates = useMemo(() => {
    return Object.entries<MaxContainerDimensions>(breakpoints).reduce(
      (acc, [breakpointKey, breakpoint]) => {
        acc[breakpointKey as TBreakpointName] = isBreakpointMet(
          dimensions,
          breakpoint
        )
        return acc
      },
      {} as Record<TBreakpointName, boolean>
    )
  }, [breakpoints, dimensions])

  useEffect(() => {
    onChange?.(breakpointStates)
  }, [breakpointStates, onChange])

  return breakpointStates
}
